import { getTranslate } from '../localization'

type GraphQLErrorShape = {
  message?: string
  extensions?: { code?: unknown }
}

type MaybeApolloError =
  | {
      message?: string
      graphQLErrors?: ReadonlyArray<GraphQLErrorShape>
      /**
       * Apollo puts a GraphQL response that arrived with a non-2xx HTTP status here rather
       * than in graphQLErrors. Reservation failures intentionally use 422, so this is still
       * a GraphQL response with a safe public code -- not a transport failure.
       */
      networkError?: unknown
    }
  | null
  | undefined

/**
 * The message to show in an error toast.
 *
 * Uses a stable server error code to select localized, client-safe copy. The code preserves the
 * actionable error class while the fixed client copy prevents a resolver message from exposing
 * implementation details, identifiers, or personal data.
 *
 * Falls back when there is no explicitly safe message to show:
 *
 *  - a network failure carries no GraphQL error, and `err.message` is a transport detail
 *    ("Failed to fetch") that tells a booking agent nothing;
 *  - a render that throws downstream of a SUCCEEDED mutation reaches Apollo's onError as a plain
 *    TypeError, whose message is an internal detail rather than an explanation;
 *  - an empty server message, since a blank toast reads as the UI itself being broken.
 *  - an unknown/internal GraphQL code, whose message is not a public contract and may disclose
 *    implementation details or personal data.
 */
// Stable codes select fixed client copy. The server's raw message is never rendered:
// even a normally safe code can be attached to an error containing identifiers or PII.
const CLIENT_SAFE_GRAPHQL_MESSAGES = new Map<string, string>([
  ['BAD_USER_INPUT', 'The request is invalid'],
  ['INVALID_ARGUMENT', 'The request is invalid'],
  ['FAILED_PRECONDITION', 'The request cannot be completed'],
  ['NOT_FOUND', 'The requested item was not found'],
  ['FORBIDDEN', 'You do not have permission to perform this action'],
  ['PERMISSION_DENIED', 'You do not have permission to perform this action'],
  ['UNAUTHENTICATED', 'Authentication is required'],
  ['RESERVATION_FAILED', 'The reservation could not be completed'],
  [
    'WALLET_INSUFFICIENT_FUNDS',
    'Insufficient wallet balance to complete this reservation'
  ],
  ['PROMOTION_FAILED', 'The reservation could not be completed'],
  ['PROMOTION_INVALID', 'Invalid promotion request'],
  ['PROMOTION_NOT_APPLICABLE', 'Promotion could not be applied'],
  ['PROMOTION_FORBIDDEN', 'Promotion is not permitted'],
  ['PROMOTION_TENANT_REQUIRED', 'Select a tenant before requesting promotions'],
  ['PROMOTION_UNAUTHENTICATED', 'Authentication is required'],
  ['PROMOTION_UNAVAILABLE', 'Promotion service is unavailable']
])

const networkGraphQLErrors = (networkError: unknown): GraphQLErrorShape[] => {
  if (!networkError || typeof networkError !== 'object') return []
  const result = (networkError as { result?: unknown }).result
  if (!result || typeof result !== 'object') return []
  const errors = (result as { errors?: unknown }).errors
  return Array.isArray(errors)
    ? errors.filter(
        (candidate): candidate is GraphQLErrorShape =>
          Boolean(candidate) && typeof candidate === 'object'
      )
    : []
}

const graphQLErrorEntries = (errors: unknown): GraphQLErrorShape[] =>
  Array.isArray(errors)
    ? errors.filter(
        (candidate): candidate is GraphQLErrorShape =>
          Boolean(candidate) && typeof candidate === 'object'
      )
    : []

export const errorToastMessage = (error: MaybeApolloError): string => {
  const graphQLErrors = [
    ...graphQLErrorEntries(error?.graphQLErrors),
    ...networkGraphQLErrors(error?.networkError)
  ]
  const safeCode = graphQLErrors.find((graphQLError) => {
    const code = graphQLError.extensions?.code
    return (
      typeof code === 'string' &&
      CLIENT_SAFE_GRAPHQL_MESSAGES.has(code) &&
      Boolean(graphQLError.message?.trim())
    )
  })?.extensions?.code

  return typeof safeCode === 'string'
    ? getTranslate(CLIENT_SAFE_GRAPHQL_MESSAGES.get(safeCode) || '')
    : getTranslate('Something went wrong')
}
